import * as RadixToast from '@radix-ui/react-toast';
import * as RadixPortal from '@radix-ui/react-portal';
import React, { ReactNode, useReducer } from 'react';
import { NotificationItem } from '../types';
import { NotificationContextProvider } from '../context';
import { popoverContainers } from '../../../core/popovers/popover-containers';
import { Toast } from './toast';
import { ToastViewport } from './toast-viewport';

interface Props {
  children: ReactNode;
}

type AddNotificationAction = {
  type: 'add';
  payload: NotificationItem;
};

type RemoveNotificationAction = {
  type: 'remove';
  payload: string;
};

type NotificationStateAction = AddNotificationAction | RemoveNotificationAction;

const notificationReducer = (state: NotificationItem[], action: NotificationStateAction) => {
  switch (action.type) {
    case 'add':
      return [...state, action.payload];
    case 'remove':
      return state
        .filter((notification) => notification.isOpen) // cleanup already closed notifications
        .map((notification) => {
          if (notification.id === action.payload) {
            return { ...notification, isOpen: false }; // set isOpen to false to trigger close animation
          }
          return notification;
        });
  }
};

export const NotificationProvider: React.FC<Props> = ({ children }) => {
  const [stack, dispatch] = useReducer(notificationReducer, []);
  const pushNotification = (notification: NotificationItem) => {
    dispatch({ type: 'add', payload: notification });
  };

  return (
    <NotificationContextProvider value={{ pushNotification }}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {stack.map(({ id, text, isOpen, icon, action }) => (
          <Toast
            key={id}
            action={action}
            icon={icon}
            open={isOpen}
            text={text}
            onOpenChange={() => {
              dispatch({ type: 'remove', payload: id });
            }}
          />
        ))}
        <RadixPortal.Root container={popoverContainers.notificationViewport()}>
          <ToastViewport />
        </RadixPortal.Root>
      </RadixToast.Provider>
    </NotificationContextProvider>
  );
};
